import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useRemoteRegistry } from "../registry/RemoteRegistryContext";
import { useAuth } from "../auth/AuthContext";
import { useNotifications } from "../context/NotificationContext";
import { iconRegistry, Home } from "./iconRegistry";
import { ManageAppsModal } from "./ManageAppsModal";
import { ApprovalQueueModal } from "./ApprovalQueueModal";
import { AdminModal } from "./AdminModal";
import { PlusCircle, ClipboardCheck, BookOpen, ShieldCheck, Bell, X, ExternalLink } from "lucide-react";
import type { ShellNotification } from "../types";

interface ShellProps {
  children: React.ReactNode;
}

function NavGroup({ label }: { label: string }) {
  return (
    <div style={{
      fontSize: "0.7rem", fontWeight: 700, color: "rgba(255,255,255,0.5)",
      textTransform: "uppercase", letterSpacing: "0.06em",
      padding: "0.75rem 1rem 0.25rem",
    }}>
      {label}
    </div>
  );
}

function NavItem({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  const location = useLocation();
  const isActive = to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <Link
      to={to}
      title={label}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        padding: "0.6rem 1rem",
        margin: "0.1rem 0.5rem",
        borderRadius: 6,
        textDecoration: "none",
        color: isActive ? "#136945" : "rgba(255,255,255,0.85)",
        background: isActive ? "#fff" : "transparent",
        fontWeight: isActive ? 600 : 400,
        fontSize: "0.9rem",
        transition: "background 0.15s",
      }}
    >
      {icon}
      <span>{label}</span>
    </Link>
  );
}

function NotificationCard({ n, onDismiss }: { n: ShellNotification; onDismiss?: (id: string) => void }) {
  return (
    <div
      style={{
        background: TYPE_BG[n.type],
        borderLeft: `4px solid ${TYPE_COLOR[n.type]}`,
        borderRadius: 6,
        padding: "0.6rem 0.75rem",
        marginBottom: "0.5rem",
        position: "relative",
      }}
    >
      {onDismiss && (
        <button
          onClick={() => onDismiss(n.id)}
          aria-label="Dismiss notification"
          style={{
            position: "absolute", top: 6, right: 6,
            background: "none", border: "none", cursor: "pointer",
            color: "#5f5f5f", padding: 2, display: "flex",
          }}
        >
          <X size={13} />
        </button>
      )}
      <div style={{ fontSize: "0.82rem", fontWeight: 600, color: TYPE_COLOR[n.type], marginBottom: 2, paddingRight: onDismiss ? 20 : 0 }}>
        {n.title}
      </div>
      {n.message && (
        <div style={{ fontSize: "0.8rem", color: "#3a3a3a", marginBottom: n.url ? 4 : 0 }}>
          {n.message}
        </div>
      )}
      {n.url && (
        <a
          href={n.url}
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: "0.78rem", color: "#136945", display: "inline-flex", alignItems: "center", gap: 3, textDecoration: "none" }}
        >
          View <ExternalLink size={11} />
        </a>
      )}
      <div style={{ fontSize: "0.72rem", color: "#888", marginTop: 4 }}>
        {n.source}
      </div>
    </div>
  );
}

const TYPE_COLOR: Record<ShellNotification["type"], string> = {
  error: "#c62828",
  warning: "#f9a825",
  info: "#136945",
};

const TYPE_BG: Record<ShellNotification["type"], string> = {
  error: "#fff0f0",
  warning: "#fff8e1",
  info: "#f0faf5",
};

function NotificationPanel({ onClose }: { onClose: () => void }) {
  const { notifications, dismissNotification, clearAll } = useNotifications();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Notifications"
      style={{
        position: "fixed",
        top: 0,
        left: 220,
        bottom: 0,
        width: 340,
        background: "#fff",
        borderRight: "1px solid #e0e0e0",
        boxShadow: "4px 0 16px rgba(0,0,0,0.12)",
        zIndex: 200,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0.75rem 1rem", borderBottom: "1px solid #e0e0e0", flexShrink: 0,
      }}>
        <span style={{ fontWeight: 600, fontSize: "0.95rem" }}>Notifications</span>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {notifications.length > 0 && (
            <button
              onClick={clearAll}
              style={{ fontSize: "0.75rem", color: "#5f5f5f", background: "none", border: "none", cursor: "pointer", padding: "2px 6px" }}
            >
              Clear all
            </button>
          )}
          <button
            onClick={onClose}
            aria-label="Close notifications"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 4, color: "#5f5f5f", display: "flex" }}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto" }}>
        {notifications.length === 0 ? (
          <p style={{ textAlign: "center", color: "#5f5f5f", fontSize: "0.85rem", marginTop: "2rem" }}>
            No notifications
          </p>
        ) : (
          <>
            {/* Persistent alerts — non-dismissible, condition-driven */}
            {notifications.filter((n) => n.persistent).length > 0 && (
              <div style={{ padding: "0.5rem 0.5rem 0" }}>
                <div style={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#5f5f5f", padding: "0.25rem 0.25rem 0.4rem" }}>
                  Active Alerts
                </div>
                {notifications.filter((n) => n.persistent).map((n) => (
                  <NotificationCard key={n.id} n={n} onDismiss={undefined} />
                ))}
              </div>
            )}

            {/* Dismissible notifications */}
            {notifications.filter((n) => !n.persistent).length > 0 && (
              <div style={{ padding: "0.5rem 0.5rem 0" }}>
                {notifications.filter((n) => n.persistent).length > 0 && (
                  <div style={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#5f5f5f", padding: "0.5rem 0.25rem 0.4rem" }}>
                    Notifications
                  </div>
                )}
                {notifications.filter((n) => !n.persistent).map((n) => (
                  <NotificationCard key={n.id} n={n} onDismiss={dismissNotification} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function Shell({ children }: ShellProps) {
  const { user, signOut } = useAuth();
  const { allRemotes, builtInRemotes, orgApps, userApps, isApprover, isAdmin, pendingCount } = useRemoteRegistry();
  const { notifications, pushNotification } = useNotifications();
  const [showManage, setShowManage] = useState(false);
  const [showApproval, setShowApproval] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  // Poll each remote's /api/notifications endpoint once on mount.
  // This surfaces alerts (e.g. active deployment freezes) regardless of whether
  // the user navigates to that app or has permission to access it.
  useEffect(() => {
    const remotesWithApi = allRemotes.filter((r) => r.apiUrl || r.devApiUrl);
    for (const remote of remotesWithApi) {
      const base = (process.env.NODE_ENV === "development" && remote.devApiUrl) ? remote.devApiUrl : remote.apiUrl;
      if (!base) continue;
      fetch(`${base}/api/notifications`)
        .then((res) => (res.ok ? res.json() : Promise.reject()))
        .then((data: { notifications: Array<{ type: "info" | "warning" | "error"; title: string; message?: string; url?: string }> }) => {
          for (const n of data.notifications ?? []) {
            pushNotification({ ...n, source: remote.label });
          }
        })
        .catch(() => {});
    }
  }, [allRemotes, pushNotification]);

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      {/* Left sidebar */}
      <nav
        style={{
          width: 220,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          borderRight: "1px solid #0f5236",
          background: "#136945",
        }}
      >
        {/* Logo / title */}
        <div style={{ height: 60, flexShrink: 0, display: "flex", alignItems: "center", gap: "0.6rem", padding: "0 1rem", borderBottom: "1px solid rgba(255,255,255,0.15)" }}>
          <img src="/skilly_dev.png" alt="" aria-hidden="true" style={{ height: 36, width: 36, objectFit: "contain", borderRadius: 4, flexShrink: 0 }} />
          <span style={{ fontWeight: 700, fontSize: "1rem", color: "#fff", lineHeight: 1 }}>Pulse</span>
        </div>

        {/* Nav items */}
        <div style={{ flex: 1, paddingTop: "0.5rem", overflowY: "auto" }}>
          <NavItem to="/" icon={<Home size={18} />} label="Home" />
          <NavItem to="/how-to" icon={<BookOpen size={18} />} label="How To" />

          {(builtInRemotes.length > 0 || orgApps.length > 0) && (
            <>
              <NavGroup label="Apps" />
              {[...builtInRemotes, ...orgApps].map((remote) => {
                const Icon = iconRegistry[remote.icon] ?? iconRegistry["LayoutDashboard"];
                return <NavItem key={remote.name} to={remote.route} icon={<Icon size={18} />} label={remote.label} />;
              })}
            </>
          )}

          {userApps.filter((a) => !orgApps.some((o) => o.url === a.url) && !builtInRemotes.some((b) => b.url === a.url)).length > 0 && (
            <>
              <NavGroup label="My Apps" />
              {userApps
                .filter((a) => !orgApps.some((o) => o.url === a.url) && !builtInRemotes.some((b) => b.url === a.url))
                .map((remote) => {
                  const Icon = iconRegistry[remote.icon] ?? iconRegistry["LayoutDashboard"];
                  return <NavItem key={remote.name} to={remote.route} icon={<Icon size={18} />} label={remote.label} />;
                })}
            </>
          )}
        </div>

        {/* Add app / Review apps buttons */}
        <button
          onClick={() => setShowManage(true)}
          title="Manage apps"
          style={{
            display: "flex", alignItems: "center", gap: "0.5rem",
            margin: "0.25rem 0.75rem", padding: "0.5rem 0.75rem",
            background: "none", border: "1px dashed rgba(255,255,255,0.35)", borderRadius: 6,
            cursor: "pointer", fontSize: "0.82rem", color: "rgba(255,255,255,0.7)",
          }}
        >
          <PlusCircle size={15} /> Add app
        </button>
        {isApprover && (
          <>
            <button
              onClick={() => setShowApproval(true)}
              title="Review app submissions"
              style={{
                display: "flex", alignItems: "center", gap: "0.5rem",
                margin: "0.25rem 0.75rem 0rem", padding: "0.5rem 0.75rem",
                background: pendingCount > 0 ? "rgba(255,255,255,0.15)" : "none",
                border: pendingCount > 0 ? "1px solid rgba(255,255,255,0.4)" : "1px dashed rgba(255,255,255,0.35)",
                borderRadius: 6, cursor: "pointer", fontSize: "0.82rem",
                color: "rgba(255,255,255,0.85)",
              }}
            >
              <ClipboardCheck size={15} />
              Review Apps
              {pendingCount > 0 && (
                <span style={{
                  background: "#fff", color: "#136945", borderRadius: 10,
                  padding: "0px 6px", fontSize: 11, fontWeight: 700, marginLeft: 2,
                }}>
                  {pendingCount}
                </span>
              )}
            </button>
          </>
        )}

        {isAdmin && (
          <button
            onClick={() => setShowAdmin(true)}
            title="Admin"
            style={{
              display: "flex", alignItems: "center", gap: "0.5rem",
              margin: "0.25rem 0.75rem 0.5rem", padding: "0.5rem 0.75rem",
              background: "none", border: "1px dashed rgba(255,255,255,0.35)",
              borderRadius: 6, cursor: "pointer", fontSize: "0.82rem",
              color: "rgba(255,255,255,0.7)",
            }}
          >
            <ShieldCheck size={15} /> Admin
          </button>
        )}

        {/* Notification bell */}
        <button
          onClick={() => setShowNotifications((v) => !v)}
          title="Notifications"
          aria-label={`Notifications${notifications.length > 0 ? ` (${notifications.length})` : ""}`}
          style={{
            display: "flex", alignItems: "center", gap: "0.5rem",
            margin: "0.25rem 0.75rem", padding: "0.5rem 0.75rem",
            background: showNotifications ? "rgba(255,255,255,0.15)" : "none",
            border: "1px solid rgba(255,255,255,0.35)",
            borderRadius: 6, cursor: "pointer", fontSize: "0.82rem",
            color: "rgba(255,255,255,0.85)", position: "relative",
          }}
        >
          <Bell size={15} />
          <span>Notifications</span>
          {notifications.length > 0 && (
            <span style={{
              background: "#c62828", color: "#fff", borderRadius: 10,
              padding: "0px 6px", fontSize: 11, fontWeight: 700, marginLeft: "auto",
            }}>
              {notifications.length}
            </span>
          )}
        </button>

        {/* User + sign out */}
        <div
          style={{
            padding: "0.75rem 1rem",
            borderTop: "1px solid rgba(255,255,255,0.15)",
            fontSize: "0.8rem",
            color: "rgba(255,255,255,0.7)",
          }}
        >
          <div style={{ marginBottom: "0.4rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {user.name ?? user.username}
          </div>
          <button
            onClick={signOut}
            style={{
              background: "none",
              border: "1px solid rgba(255,255,255,0.35)",
              color: "rgba(255,255,255,0.8)",
              borderRadius: 4,
              padding: "0.2rem 0.6rem",
              cursor: "pointer",
              fontSize: "0.8rem",
              width: "100%",
            }}
          >
            Sign out
          </button>
        </div>
      </nav>

      {/* Main content */}
      <main style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {children}
      </main>

      {showManage && <ManageAppsModal onClose={() => setShowManage(false)} />}
      {showApproval && <ApprovalQueueModal onClose={() => setShowApproval(false)} />}
      {showAdmin && <AdminModal onClose={() => setShowAdmin(false)} />}
      {showNotifications && <NotificationPanel onClose={() => setShowNotifications(false)} />}
    </div>
  );
}


